/* eslint-disable max-len */
/* eslint-disable no-console, react/prop-types */
import {InfoCircleOutlined, LockFilled} from '@ant-design/icons';
import {Flex, Tooltip} from 'antd';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import industryBenchmarkBg from '../../../../../assets/skeleton/industryBenchmark.png';
import OverviewBarChart from '../../../../../components/Chart/BarChart/OverviewBarChart';
import PilComp from '../../../../../components/DisplayComponent/PilComp';
import UpgradeButton from '../../../../../components/UpgradeButton/UpgradeButton';
import {Routes} from '../../../../../constants/Routes';
import {formatIndian} from '../../../../../utils/formatIndian';

import {
  convertScientificToDecimal,
  formatBenchmarkValue,
  TCO2E_TO_KT_THRESHOLD
} from './utils';

const PILL_ABOVE_COLOR = '#EB606A';
const PILL_BELOW_COLOR = '#2D3D25';
const PILL_EQUAL_COLOR = '#F3F4F8';
const BENCHMARK_BAR_COLOR = '#023A5B';
const COMPANY_ABOVE_COLOR = '#F48088';
const COMPANY_BELOW_COLOR = '#91C577';

const formatTooltipValue = (
  value: number,
  unit: string,
  comparisonValue?: number
): {formatted: string; unit: string} => {
  const shouldConvertToKt =
    unit === 'tCO2e' &&
    (Math.abs(value) > TCO2E_TO_KT_THRESHOLD ||
      (typeof comparisonValue === 'number' &&
        Math.abs(comparisonValue) > TCO2E_TO_KT_THRESHOLD));

  if (shouldConvertToKt) {
    return {
      formatted: formatIndian(convertScientificToDecimal(value / 1000)),
      unit: 'KtCO2e'
    };
  }

  return {formatted: formatIndian(convertScientificToDecimal(value)), unit};
};

interface BenchmarkCardItem {
  id: string;
  label: string;
  value: number;
  benchmark: number;
  unit: string;
  tooltip?: string;
}

const BenchmarkCard: React.FC<{
  item: BenchmarkCardItem;
  isBlocked?: boolean;
}> = ({item, isBlocked = false}) => {
  const navigate = useNavigate();
  const isAbove = item.value > item.benchmark;
  const isBelow = item.value < item.benchmark;
  const isEqual = !isAbove && !isBelow;

  const pillColor = isAbove
    ? PILL_ABOVE_COLOR
    : isBelow
      ? PILL_BELOW_COLOR
      : PILL_EQUAL_COLOR;
  const companyBarColor = isAbove ? COMPANY_ABOVE_COLOR : COMPANY_BELOW_COLOR;

  const cardStatusClass = isAbove
    ? 'industryBenchmarkCard--above'
    : isBelow
      ? 'industryBenchmarkCard--below'
      : 'industryBenchmarkCard--equal';

  const formattedValue = formatBenchmarkValue(
    item.value,
    item.unit,
    item.benchmark
  );
  const formattedBenchmark = formatBenchmarkValue(
    item.benchmark,
    item.unit,
    item.value
  );
  const tooltipValue = formatTooltipValue(
    item.value,
    item.unit,
    item.benchmark
  );
  const tooltipBenchmark = formatTooltipValue(
    item.benchmark,
    item.unit,
    item.value
  );

  const chartData = [
    {
      source: 'Benchmark',
      value: item.benchmark,
      unit: item.unit,
      displayValue: tooltipBenchmark.formatted,
      displayUnit: tooltipBenchmark.unit,
      fill: BENCHMARK_BAR_COLOR
    },
    {
      source: 'Company',
      value: item.value,
      unit: item.unit,
      displayValue: tooltipValue.formatted,
      displayUnit: tooltipValue.unit,
      fill: companyBarColor
    }
  ];

  if (isBlocked) {
    return (
      <div
        className="industryBenchmarkCard industryBenchmarkCard--locked"
        style={{
          backgroundImage: `url(${industryBenchmarkBg})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center'
        }}
      >
        <Flex
          className="industryBenchmarkCardHeader"
          justify="space-between"
          align="center"
          gap={8}
        >
          <PilComp
            value={item.label}
            color="#F5DA84"
            style={{
              padding: '6px 12px',
              color: '#101223',
              font: 'normal normal 700 12px Montserrat'
            }}
          />
          <Tooltip title={item.tooltip}>
            <InfoCircleOutlined className="industryBenchmarkCardInfo" />
          </Tooltip>
        </Flex>
        <Flex
          className="industryBenchmarkLockedWrap"
          vertical
          align="center"
          gap={18}
        >
          <div className="industryBenchmarkLockIconWrap">
            <LockFilled className="industryBenchmarkLockIcon" />
          </div>
          <Flex vertical align="center" gap={2}>
            <span className="industryBenchmarkLockedTitle">
              Unlock Advanced
            </span>
            <span className="industryBenchmarkLockedSubtitle">
              Scope 3 insights
            </span>
          </Flex>
          <div
            onClick={() => {
              navigate(Routes.PRICING_DETAILS);
            }}
          >
            <UpgradeButton
              title="Upgrade"
              style={{
                height: 29,
                minWidth: 102,
                borderRadius: 4,
                font: 'normal normal 700 14px Montserrat',
                color: '#1B1D2A'
              }}
            />
          </div>
        </Flex>
      </div>
    );
  }

  return (
    <div className={`industryBenchmarkCard ${cardStatusClass}`}>
      <Flex
        className="industryBenchmarkCardHeader"
        justify="space-between"
        align="center"
        gap={8}
      >
        <PilComp
          value={item.label}
          color={pillColor}
          style={{
            padding: '6px 12px',
            color: isEqual ? '#374151' : '#fff',
            font: 'normal normal 700 12px Montserrat'
          }}
        />
        <Tooltip title={item.tooltip}>
          <InfoCircleOutlined className="industryBenchmarkCardInfo" />
        </Tooltip>
      </Flex>
      <Flex className="industryBenchmarkCardLegend" gap={12}>
        <Flex align="center" gap={6}>
          <span
            className="industryBenchmarkLegendDot"
            style={{background: BENCHMARK_BAR_COLOR}}
          />
          <span className="industryBenchmarkLegendText">Benchmark Value</span>
        </Flex>
        <Flex align="center" gap={6}>
          <span
            className="industryBenchmarkLegendDot"
            style={{background: companyBarColor}}
          />
          <span className="industryBenchmarkLegendText">Company Value</span>
        </Flex>
      </Flex>
      <div className="industryBenchmarkChartWrap">
        <div className="industryBenchmarkChartInner" style={{height: 140}}>
          <OverviewBarChart data={chartData} />
        </div>
      </div>
      <div className="industryBenchmarkDataArea">
        <div className="industryBenchmarkCompanyRow">
          <Flex gap={4} justify="center">
            <span className="industryBenchmarkValueBold">
              {formattedValue.formatted}
            </span>
            <span className="industryBenchmarkValueSmall">
              {formattedValue.unit}
            </span>
          </Flex>
          <span className="industryBenchmarkValueLabel">Company</span>
        </div>
        <div className="industryBenchmarkDottedLine" />
        <div className="industryBenchmarkCompanyRow">
          <Flex gap={4} justify="center">
            <span className="industryBenchmarkValueBold">
              {formattedBenchmark.formatted}
            </span>
            <span className="industryBenchmarkValueSmall">
              {formattedBenchmark.unit}
            </span>
          </Flex>
          <span className="industryBenchmarkValueLabel">Benchmark</span>
        </div>
      </div>
    </div>
  );
};

export default BenchmarkCard;
